import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Descriptions, Button, Spin, message, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModuleHeader from "../../components/ModuleHeader";
import RiderFormModal from "./RiderFormModal";
import { getRiderById } from "../../services/riders.service";
import dayjs from "dayjs";

export default function RiderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rider, setRider] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchRider = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRiderById(id);
      setRider(response.data);
    } catch (error) {
      message.error("Failed to load rider details");
      navigate("/delivery-partners");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRider();
  }, [id, fetchRider]);

  const handleBack = () => {
    navigate("/delivery-partners");
  };

  const handleEdit = () => {
    setModalVisible(true);
  };

  const handleModalClose = (refresh) => {
    setModalVisible(false);
    if (refresh) {
      fetchRider();
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!rider) {
    return null;
  }

  return (
    <div>
      <ModuleHeader
        title="Delivery Partners"
        subtitle={rider.fullName}
        backTo="/delivery-partners"
        onBack={handleBack}
      />

      <div style={{ padding: "0 24px 24px" }}>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: "#6b7280", fontSize: 13 }}>
              Rider ID: {rider.riderId}
            </span>
            <Tag
              color={
                rider.status === "active"
                  ? "green"
                  : rider.status === "deleted"
                    ? "red"
                    : "default"
              }
            >
              {rider.status}
            </Tag>
          </div>
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            Edit
          </Button>
        </div>

      {/* Profile Card */}
      <Card title="Profile Information" style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Full Name">{rider.fullName}</Descriptions.Item>
          <Descriptions.Item label="Rider ID">{rider.riderId}</Descriptions.Item>
          <Descriptions.Item label="External Rider ID">
            {rider.externalRiderId || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Nationality">{rider.nationality?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Mobile Number">{rider.mobileNumber}</Descriptions.Item>
          <Descriptions.Item label="WhatsApp Number">{rider.whatsappNumber || "—"}</Descriptions.Item>
          <Descriptions.Item label="Email Address">{rider.emailAddress || "—"}</Descriptions.Item>
          <Descriptions.Item label="Civil ID">{rider.civilId}</Descriptions.Item>
          <Descriptions.Item label="Date of Joining">
            {rider.dateOfJoining ? dayjs(rider.dateOfJoining).format("DD/MM/YYYY") : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Designation">{rider.designation?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Project">{rider.project?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Zone">{rider.zone?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="City">{rider.city?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Fleet Type">{rider.fleetType?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={rider.status === "active" ? "green" : "default"}>{rider.status}</Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Bank Details Card */}
      <Card title="Bank & Salary Details" style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Salary Scheme">{rider.salaryScheme?.name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Bank Name">{rider.bankName?.name || rider.bankName?.displayName || "—"}</Descriptions.Item>
          <Descriptions.Item label="Account Number">{rider.bankAccountNo}</Descriptions.Item>
          <Descriptions.Item label="IBAN">{rider.iban || "—"}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Coming Soon Sections */}
      <Card title="Documents" style={{ marginBottom: 24 }}>
        <p style={{ color: "#94a3b8", margin: 0 }}>Document management coming soon</p>
      </Card>

      <Card title="Inventory & Vehicles" style={{ marginBottom: 24 }}>
        <p style={{ color: "#94a3b8", margin: 0 }}>Inventory and vehicle management coming soon</p>
      </Card>

      <Card title="Leave History">
        <p style={{ color: "#94a3b8", margin: 0 }}>Leave history coming soon</p>
      </Card>

      {/* Modal */}
      {modalVisible && (
        <RiderFormModal
          visible={modalVisible}
          rider={rider}
          onClose={handleModalClose}
        />
      )}
      </div>
    </div>
  );
}
